/**
 * Environment store — which Opus environment the user is targeting.
 *
 * Opus is multi-tenant (PROD/STAGING/etc + org switcher). The top bar shows
 * the current selection and the API base reads it to scope every request.
 *
 * Persisted because the user typically stays in the same env for the day.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type EnvKind = "prod" | "staging" | "dev";

export interface Environment {
  kind: EnvKind;
  /** Human-readable org/tenant slug shown in the env switcher. */
  org: string;
}

interface EnvState {
  current: Environment;
  /** All environments the user can switch to — populated from /api/environments. */
  available: Environment[];

  set: (env: Environment) => void;
  setAvailable: (envs: Environment[]) => void;
}

const DEFAULT_ENV: Environment = { kind: "prod", org: "guesttek-meraki" };

export const useEnvironment = create<EnvState>()(
  persist(
    (set) => ({
      current: DEFAULT_ENV,
      available: [DEFAULT_ENV],

      set: (env) => set({ current: env }),
      setAvailable: (envs) => set({ available: envs }),
    }),
    {
      name: "opus.environment",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ current: s.current }),
    },
  ),
);
